import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

type Props = {
  onCapture: (blob: Blob) => Promise<void> | void;
  onConfirm?: () => void;
};

export function CameraCapture({ onCapture, onConfirm }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    async function start() {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 1024 },
            height: { ideal: 1024 },
          },
          audio: false,
        });
        setStream(s);
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          await videoRef.current.play();
        }
      } catch (err: any) {
        toast.error("Camera access failed", {
          description: err?.message || "Unknown error",
        });
      }
    }

    if (!capturedUrl) start();
    return () => {
      stream?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capturedUrl]);

  async function capture() {
    if (!videoRef.current) return;
    setCapturing(true);
    try {
      const video = videoRef.current;
      const size = Math.min(
        video.videoWidth || 1024,
        video.videoHeight || 1024,
      );
      const canvas = document.createElement("canvas");
      canvas.width = 1024;
      canvas.height = 1024;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("canvas_ctx_unavailable");

      // Center-crop to square.
      const sx = (video.videoWidth - size) / 2;
      const sy = (video.videoHeight - size) / 2;
      ctx.drawImage(video, sx, sy, size, size, 0, 0, 1024, 1024);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("capture_failed"))),
          "image/jpeg",
          0.92,
        );
      });

      const url = URL.createObjectURL(blob);
      setCapturedUrl(url);
      setCapturedBlob(blob);
    } catch (err: any) {
      toast.error("Capture failed", {
        description: err?.message || "Unknown error",
      });
    } finally {
      setCapturing(false);
    }
  }

  function retake() {
    if (capturedUrl) URL.revokeObjectURL(capturedUrl);
    setCapturedUrl(null);
    setCapturedBlob(null);
  }

  async function confirm() {
    if (!capturedBlob) return;
    setConfirming(true);
    try {
      await onCapture(capturedBlob);
      toast.success("Selfie saved");
      onConfirm?.();
    } catch (err: any) {
      toast.error("Upload failed", {
        description: err?.message || "Unknown error",
      });
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="mx-auto aspect-square w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-black/30">
        {capturedUrl ? (
          <img
            src={capturedUrl}
            className="h-full w-full object-cover"
            alt="Selfie preview"
          />
        ) : (
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            playsInline
            muted
          />
        )}
      </div>

      <div className="flex gap-4">
        {capturedUrl ? (
          <>
            <Button
              className="h-14 flex-1 text-lg"
              variant="outline"
              onClick={retake}
              disabled={confirming}
            >
              Retake
            </Button>
            <Button
              className="h-14 flex-1 text-lg"
              onClick={confirm}
              disabled={confirming}
            >
              {confirming ? "Saving…" : "Use This Photo"}
            </Button>
          </>
        ) : (
          <Button
            className="h-14 flex-1 text-lg"
            onClick={capture}
            disabled={capturing}
          >
            {capturing ? "Capturing…" : "Capture"}
          </Button>
        )}
      </div>
    </div>
  );
}
